// `scriptctl ingest` — bare command (§4). Converts a txt/md/docx or video source
// into a Script v4 workspace (script.jsonl → script.initial.json → script.json).
// Fully decoupled from `direct`: touches only ingest domain/infra + llm + common.

import * as path from "node:path";

import { CliError, EXIT_INPUT, EXIT_OK, type CommandOutput } from "../../common.js";
import { resolveSource } from "../../infra/ingest/source-resolver.js";
import { PRODUCTION_LLM_COMPOSITION, type LlmComposition } from "../../llm/composition.js";
import type {
  LlmExecutorOptions,
  VideoProviderCredential,
  VideoProviderOverrides,
  VideoUploadMode,
} from "../../llm/types.js";
import { runTextIngest } from "./pipeline.js";
import { runVideoIngest } from "./video-pipeline.js";

type Dict = Record<string, unknown>;
const strOf = (v: unknown): string => (v === null || v === undefined ? "" : String(v));
const intOf = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const VIDEO_OPTION_KEYS = [
  "upload_mode",
  "provider_base_url",
  "provider_api_key",
  "provider_api_key_env",
  "provider_model",
  "step",
] as const;

function invalidVideoOption(message: string, actual: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: "INGEST_VIDEO_OPTION_INVALID",
    hint: "Use these options only with a video file or video episode directory.",
    data: { actual },
  });
}

function optionProvided(opts: Dict, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(opts, key) && opts[key] !== undefined;
}

function requiredOptionValue(opts: Dict, key: string, flag: string): string | undefined {
  if (!optionProvided(opts, key)) return undefined;
  const value = strOf(opts[key]).trim();
  if (!value) throw invalidVideoOption(`${flag} requires a non-empty value.`, [flag]);
  return value;
}

function uploadModeOf(opts: Dict): VideoUploadMode {
  const mode = requiredOptionValue(opts, "upload_mode", "--upload-mode") ?? "file-api";
  if (mode !== "file-api" && mode !== "base64") {
    throw invalidVideoOption("Video upload mode must be file-api or base64.", [`--upload-mode ${mode}`]);
  }
  return mode;
}

function providerOverridesOf(opts: Dict): VideoProviderOverrides | undefined {
  const literalKeyProvided = optionProvided(opts, "provider_api_key");
  const keyEnvProvided = optionProvided(opts, "provider_api_key_env");
  if (literalKeyProvided && keyEnvProvided) {
    throw invalidVideoOption("Provider API key options are mutually exclusive.", [
      "--provider-api-key",
      "--provider-api-key-env",
    ]);
  }
  const baseUrl = requiredOptionValue(opts, "provider_base_url", "--provider-base-url");
  const literalKey = requiredOptionValue(opts, "provider_api_key", "--provider-api-key");
  const keyEnv = requiredOptionValue(opts, "provider_api_key_env", "--provider-api-key-env");
  const model = requiredOptionValue(opts, "provider_model", "--provider-model");
  if (keyEnv !== undefined && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(keyEnv)) {
    throw invalidVideoOption("Provider API key environment variable name is invalid.", [keyEnv]);
  }
  let apiKey: VideoProviderCredential | undefined;
  if (literalKey !== undefined) apiKey = { kind: "literal", value: literalKey };
  else if (keyEnv !== undefined) apiKey = { kind: "env", name: keyEnv };
  if (!baseUrl && !apiKey && !model) return undefined;
  return {
    ...(baseUrl ? { baseUrl } : {}),
    ...(apiKey ? { apiKey } : {}),
    ...(model ? { model } : {}),
  };
}

export async function commandIngest(opts: Dict, composition: LlmComposition = PRODUCTION_LLM_COMPOSITION): Promise<[CommandOutput, number]> {
  const sourcePath = strOf(opts["source_path"]).trim();
  if (!sourcePath) {
    throw new CliError("ingest needs a source file or directory.", {
            exitCode: EXIT_INPUT,
            errorCode: "INGEST_NO_SOURCE",
            hint: "Pass a txt/md/docx file or a video file/directory: scriptctl ingest --source-path <path>.",
            data: { expected: ["--source-path <file|dir>"] },
          });
  }
  // Resolve to an absolute path so the workspace location is explicit and
  // independent of cwd — the default "workspace" would otherwise land silently
  // under wherever the command happened to run. Downstream (view / publish /
  // ingest status / the runtime watcher) then all agree on one concrete dir.
  const workspacePath = path.resolve(strOf(opts["workspace_path"] || "workspace"));
  const resolved = resolveSource(sourcePath);
  const explicitVideoOptions = VIDEO_OPTION_KEYS.filter((key) => {
    const value = opts[key];
    return typeof value === "boolean" ? value : optionProvided(opts, key);
  });
  if (resolved.kind === "text" && explicitVideoOptions.length > 0) {
    throw invalidVideoOption("Video ingest options cannot be used with a text source.", explicitVideoOptions.map((key) => `--${key.replace(/_/g, "-")}`));
  }

  if (resolved.kind !== "text") {
    const uploadMode = uploadModeOf(opts);
    const videoProvider = providerOverridesOf(opts);
    const executorOptions: LlmExecutorOptions | undefined = videoProvider ? { videoProvider } : undefined;
    const executor = composition.createExecutor(strOf(opts["llm_config"]).trim() || undefined, executorOptions);
    const configuredRoute = executor.route("ingest.video-serial");
    const report = await runVideoIngest({
      sourcePath,
      workspacePath,
      executor,
      fps: intOf(opts["fps"], 3),
      force: Boolean(opts["force"]),
      step: Boolean(opts["step"]),
      uploadMode,
    });
    const result = [
      `workspace: ${report.workspacePath}`,
      `${report.completed}/${report.episodes} episodes complete (${report.processed} processed / ${report.reused} reused / ${report.remaining} remaining) → ${report.scriptPath}`,
      ...(report.skipped > 0 ? [`skipped: ${report.skippedEpisodes.join(", ")}`] : []),
      `assets: ${report.assets.actors} actors / ${report.assets.locations} locations / ${report.assets.props} props`,
    ];
    const actualRoute = [...executor.usageEvents()].reverse().find((event) =>
      event.task === "ingest.video-serial" && event.status === "succeeded"
    ) ?? configuredRoute;
    return [{
      text: result.join("\n"),
      data: {
        workspace: report.workspacePath,
        script_path: report.scriptPath,
        episodes: report.episodes,
        processed: report.processed,
        reused: report.reused,
        parsed: report.completed - report.skipped,
        skipped: report.skipped,
        skipped_episodes: report.skippedEpisodes,
        has_skips: report.skipped > 0,
        completed: report.completed,
        remaining: report.remaining,
        complete: report.complete,
        next_episode: report.nextEpisode,
        upload_mode: report.uploadMode,
        route: {
          protocol: actualRoute.protocol,
          provider: actualRoute.provider,
          base_url: actualRoute.baseUrl,
          model: actualRoute.model,
        },
        assets: report.assets,
      },
    }, EXIT_OK];
  }

  const executor = composition.createExecutor(strOf(opts["llm_config"]).trim() || undefined);
  const report = await runTextIngest({
    sourcePath,
    workspacePath,
    executor,
    concurrency: intOf(opts["concurrency"], 30),
    force: Boolean(opts["force"]),
  });

  const result = [
    `workspace: ${report.workspacePath}`,
    `${report.records} records, ${report.chunks} chunks → ${report.scriptPath}`,
    `assets: ${report.assets.actors} actors / ${report.assets.locations} locations / ${report.assets.props} props`,
  ];
  return [{
    text: result.join("\n"),
    data: {
      workspace: report.workspacePath,
      script_path: report.scriptPath,
      records: report.records,
      chunks: report.chunks,
      assets: report.assets,
    },
  }, EXIT_OK];
}
